import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Loader2, AlertTriangle, Clock, CheckCircle, DollarSign, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BUCKET_CONFIG = {
  current: { label: 'Current', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  '1-30': { label: '1-30 Days', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  '31-60': { label: '31-60 Days', color: 'bg-orange-100 text-orange-700', icon: Clock },
  '61-90': { label: '61-90 Days', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  '90+': { label: '90+ Days', color: 'bg-red-200 text-red-800', icon: AlertTriangle }
};

export default function InvoiceAgingPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/invoice-aging`);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load aging report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/reports/invoice-aging/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_aging_${report?.as_of_date || 'report'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel file downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const buckets = [
    { key: 'current', data: report?.current || [] },
    { key: '1-30', data: report?.days_1_30 || [] },
    { key: '31-60', data: report?.days_31_60 || [] },
    { key: '61-90', data: report?.days_61_90 || [] },
    { key: '90+', data: report?.days_90_plus || [] }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Invoice Aging Report
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Track overdue invoices by 30/60/90 day buckets • As of {report?.as_of_date}
            </p>
          </div>
          <Button 
            onClick={handleExportExcel} 
            disabled={exporting || !report}
            className="gap-2"
            data-testid="export-aging-excel-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Export to Excel
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {buckets.map(({ key, data }) => {
            const config = BUCKET_CONFIG[key];
            const total = report?.totals?.[key === 'current' ? 'current' : `days_${key.replace('-', '_').replace('+', '_plus')}`] || 0;
            return (
              <Card key={key} className={`${data.length > 0 && key !== 'current' ? 'ring-2 ring-offset-2 ring-amber-300' : ''}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <config.icon className={`w-4 h-4 ${key === 'current' ? 'text-green-600' : key.includes('90') ? 'text-red-600' : 'text-amber-600'}`} />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${config.color}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className="text-2xl font-bold font-mono">{formatCurrency(total)}</p>
                  <p className="text-xs text-slate-500 mt-1">{data.length} invoice{data.length !== 1 ? 's' : ''}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Total */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <span className="font-semibold">Total Outstanding</span>
              </div>
              <span className="text-2xl font-bold font-mono">{formatCurrency(report?.totals?.total || 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Aging Buckets */}
        <div className="flex justify-end">
          <Input
            type="search"
            placeholder="Search by customer or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
            data-testid="aging-search-input"
          />
        </div>
        {buckets.filter(b => b.data.length > 0).map(({ key, data }) => {
          const config = BUCKET_CONFIG[key];
          const filteredData = data.filter((invoice) => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            return (invoice.customer_name || '').toLowerCase().includes(q)
              || (invoice.description || '').toLowerCase().includes(q);
          });
          if (filteredData.length === 0) return null;
          return (
            <Card key={key}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className={`px-2 py-1 rounded text-sm ${config.color}`}>
                    {config.label}
                  </span>
                  <span className="text-slate-500 font-normal">
                    {filteredData.length} invoice{filteredData.length !== 1 ? 's' : ''}
                    {searchQuery && filteredData.length !== data.length && (
                      <span className="ml-1 text-xs">(filtered from {data.length})</span>
                    )}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Days Overdue</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-mono text-sm">{invoice.invoice_date}</TableCell>
                        <TableCell className="font-medium">{invoice.customer_name}</TableCell>
                        <TableCell className="text-slate-500 max-w-xs truncate">{invoice.description || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{invoice.expected_payment_date}</TableCell>
                        <TableCell className="text-right">
                          {invoice.days_overdue > 0 ? (
                            <span className={`font-medium ${invoice.days_overdue > 60 ? 'text-red-600' : invoice.days_overdue > 30 ? 'text-orange-600' : 'text-amber-600'}`}>
                              {invoice.days_overdue} days
                            </span>
                          ) : (
                            <span className="text-green-600">On time</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(invoice.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}

        {/* No invoices */}
        {buckets.every(b => b.data.length === 0) && (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No invoices to display</p>
              <p className="text-xs text-slate-400 mt-1">Add invoices to see the aging report</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
